import type { AxiosError, AxiosInstance } from "axios";

import i18n from "@/configs/i18n";
import { hardLogout } from "@/lib/session";
import { useAuthStore } from "@/store/use-auth-store";

import { HARD_LOGOUT_ERROR_CODES, RETRY_FLAG } from "./constants";
import {
  getErrorCode,
  getErrorMessage,
  getRequestId,
  showErrorToast,
  updateRequestAuth,
} from "./helpers";
import { getInflightRefresh, isSessionRejected, refreshSessionOnce } from "./refresh";
import type { RetryConfig } from "./types";

/** So'rovga qo'yilgan `Bearer` qiymati — qaysi token bilan ketganini bilish uchun. */
const sentToken = (config: RetryConfig): string | undefined => {
  const header = config.headers?.Authorization ?? config.headers?.authorization;
  return typeof header === "string" ? header.replace(/^Bearer\s+/i, "") : undefined;
};

/**
 * Axios nusxasiga interceptorlarni o'rnatadi: autentifikatsiya, xatolarni
 * ko'rsatish va token yangilash mantig'i shu yerdan keladi.
 *
 * @param instance - interceptorlar o'rnatiladigan axios nusxasi
 */
export function setupInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    async config => {
      // Yangilash ketayotgan bo'lsa (reload'dan keyingi tiklash yoki boshqa
      // so'rovning 401 i) so'rov uni KUTADI. Ilgari reload'da `/auth/me`
      // token'siz ketib 401 olardi va ikkinchi yangilashni boshlab yuborardi
      // — backend esa ikkinchisini o'g'irlik deb sessiyani yopardi.
      const pending = getInflightRefresh();
      if (pending) await pending.catch(() => undefined);

      const token = useAuthStore.getState().data?.accessToken;
      if (token) config.headers.Authorization = `Bearer ${token}`;

      const language = i18n.language || i18n.resolvedLanguage || "uz";
      config.headers["Accept-Language"] = language;

      return config;
    },
    error => Promise.reject(error),
  );

  instance.interceptors.response.use(
    response => response.data,
    async (error: AxiosError) => {
      const originalRequest = (error.config ?? {}) as RetryConfig;
      const status = error.response?.status;
      const code = getErrorCode(error);

      // Qaytarib bo'lmaydigan holatlar: token qayta ishlatilgan, parol
      // o'zgargani uchun eskirgan yoki hisob bloklangan. Yangilashga
      // urinmaymiz — sessiya shu yerda tugaydi.
      if (code && HARD_LOGOUT_ERROR_CODES.has(code)) {
        if (useAuthStore.getState().auth) {
          showErrorToast(getErrorMessage(error), getRequestId(error));
          hardLogout();
        }
        throw error;
      }

      if (status === 401 && !originalRequest[RETRY_FLAG] && useAuthStore.getState().auth) {
        originalRequest[RETRY_FLAG] = true;

        // So'rov eski token bilan ketgan, bu orada esa boshqa yo'l allaqachon
        // yangilab bo'lgan — qayta yangilash shart emas.
        const current = useAuthStore.getState().data?.accessToken;
        if (current && current !== sentToken(originalRequest)) {
          return instance.request(updateRequestAuth(originalRequest, current));
        }

        let accessToken: string;
        try {
          const session = await refreshSessionOnce();
          useAuthStore.getState().setAuth(session);
          accessToken = session.accessToken;
        } catch (refreshError) {
          // Faqat server "sessiya yo'q" desa chiqamiz. Tarmoq uzilishi yoki
          // backend qayta ishga tushayotgani — foydalanuvchining aybi emas.
          if (isSessionRejected(refreshError) && useAuthStore.getState().auth) {
            showErrorToast(i18n.t("COMMON.SESSION_EXPIRED"));
            hardLogout();
          }
          throw refreshError;
        }

        // Takroriy so'rov o'z xatosini o'zi aytadi (RETRY_FLAG qo'yilgan).
        return instance.request(updateRequestAuth(originalRequest, accessToken));
      }

      /**
       * Ilgari HAR QANDAY 401 jim yutilardi va UI shunchaki to'xtardi:
       * na toast, na konsolda iz. Endi faqat token yangilash yo'li jim
       * qoladi (u allaqachon SESSION_EXPIRED ni ko'rsatgan), qolgani
       * `getErrorMessage` orqali aytiladi.
       *
       * `skipErrorToast` — ataylab 404 qaytaradigan ixtiyoriy GET'lar uchun.
       */
      const silencedByRefresh = status === 401 && originalRequest[RETRY_FLAG] === true;

      if (!silencedByRefresh && !originalRequest.skipErrorToast) {
        showErrorToast(getErrorMessage(error), getRequestId(error));
      }
      throw error;
    },
  );
}
